<template>
  <div class="flex flex-col gap-2">
    <p>Preview:</p>
    <div class="relative">
      <div ref="iframeWrapperRef" class="flex flex-col" v-html="embedHtml"></div>
      <div v-if="isLoading" :class="className" data-testid="embed-preview-loader">
        <Icon class="dark:text-zinc-500" name="general.spinner" size="base" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useResizeObserver } from '@vueuse/core';
import { Icon } from '@/storybook/icon';
import { useEmbedStore } from '@/modules/viewer/store/useEmbedStore';

const embedStore = useEmbedStore();
const { embedHtml } = storeToRefs(embedStore);
const iframeWrapperRef = ref(null);
const isLoading = ref(!embedHtml.value);

useResizeObserver(iframeWrapperRef, entries => {
  const el = entries[0].target as HTMLElement;
  const iframe = el.getElementsByTagName('iframe')[0];
  const { height, width } = el.getBoundingClientRect();
  if (!iframe || !height || !width) {
    return;
  }

  const iframeWidth: number = Number(iframe.width);

  if (iframeWidth && width < iframeWidth) {
    iframe.width = `${width}`;
    iframe.height = `${(width / iframeWidth) * height}`;
  }
});

const className = computed(() => {
  return {
    'flex justify-center items-center bg-slate-100 dark:bg-zinc-700 rounded-10': true,
    'min-h-[240px]': !embedHtml.value,
    'absolute top-0 right-0 bottom-0 left-0': embedHtml.value,
  };
});

// Watch for changes in embedHtml
watch(embedHtml, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    // Wait for the DOM to update
    await nextTick();

    // Get the iframe from the DOM
    const el = iframeWrapperRef.value;
    const iframe = el ? (el as HTMLElement).getElementsByTagName('iframe')[0] : null;

    if (iframe) {
      isLoading.value = true;
      const iframeLoadHandler = () => {
        isLoading.value = false;
        iframe.removeEventListener('load', iframeLoadHandler);
      };

      iframe.addEventListener('load', iframeLoadHandler);
    }
  }
});
</script>
